import React from 'react';
import { Surface } from '../layout/Surface';
import { cn } from '../../lib/utils';

/**
 * PaneShell — one widget of the project console.
 *
 * Every pane is a `tile`, the same skin the Tasks board columns use. That is
 * deliberate: `tile` was written for exactly this case — several coplanar
 * surfaces on screen at once, shallow elevation, no backdrop-blur (five blur
 * passes for a material almost entirely covered by opaque content is not a
 * trade worth making), and chrome that survives below `md` so the mobile stack
 * doesn't run together.
 *
 * The header is deliberately slim. In a viewport-locked console every pixel a
 * header takes is a pixel of content five times over, so this is a 32px
 * instrument label — uppercase, tracking-widest, muted — rather than the 56px
 * CardHeader the old dashboard used.
 *
 * The body is `flex-1 min-h-0 overflow-y-auto`: panes scroll, the page never
 * does. That inversion is what makes resizing mean anything.
 */

/**
 * Layout for a `<Panel>` that holds a PaneShell.
 *
 * `overflow: visible` is the important one. react-resizable-panels sets
 * `overflow: hidden` inline on every Panel, which would shear the tile's drop
 * shadow off at each pane edge — the exact thing the project Layout refuses to
 * do to its surfaces ("no overflow-hidden either: it would clip the surfaces'
 * shadows into the gap"). Only the `style` prop can beat an inline value.
 *
 * The zeroed min sizes are not decoration. `overflow: hidden` is what was
 * forcing each Panel's automatic minimum size to 0; take it away and the
 * min-content floor comes back, and one long file name could push a pane wider
 * than its share of the split.
 */
export const PANE_STYLE = { overflow: 'visible', minWidth: 0, minHeight: 0 };

export function PaneShell({
    icon: Icon,
    label,
    count,
    accent = 'text-muted-foreground',
    action,
    children,
    className,
    bodyClassName,
    // Panes whose content manages its own scrolling (the composer) opt out.
    scroll = true,
}) {
    return (
        <Surface variant="tile" className={cn('h-full', className)}>
            <header className="flex h-9 flex-shrink-0 items-center gap-2 border-b border-white/[0.06] px-3">
                {Icon ? <Icon className={cn('h-3.5 w-3.5 flex-shrink-0', accent)} /> : null}
                <h2 className="truncate text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                    {label}
                </h2>
                {count > 0 ? (
                    <span className="flex-shrink-0 rounded-full bg-white/[0.07] px-1.5 text-[10px] font-bold tabular-nums text-muted-foreground">
                        {count}
                    </span>
                ) : null}
                {action ? <div className="ml-auto flex flex-shrink-0 items-center">{action}</div> : null}
            </header>

            <div className={cn('flex min-h-0 flex-1 flex-col', scroll && 'overflow-y-auto', bodyClassName)}>
                {children}
            </div>
        </Surface>
    );
}

/**
 * The "nothing here" state. Centred rather than top-aligned, because a pane can
 * be dragged to any height and a top-aligned empty state looks like a loading
 * failure in a tall one.
 */
export function PaneEmpty({ icon: Icon, title, hint }) {
    return (
        <div className="flex flex-1 flex-col items-center justify-center gap-1.5 px-4 py-6 text-center">
            {Icon ? <Icon className="h-5 w-5 text-muted-foreground/30" /> : null}
            <p className="text-xs text-muted-foreground">{title}</p>
            {hint ? <p className="text-[10px] text-muted-foreground/60">{hint}</p> : null}
        </div>
    );
}

/**
 * A header affordance that navigates somewhere fuller — "All", "Open board".
 * Small enough not to compete with the pane label it sits beside.
 */
export function PaneLink({ children, onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="rounded-md px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground transition-colors hover:bg-white/5 hover:text-primary"
        >
            {children}
        </button>
    );
}

export default PaneShell;
